import type { Edge, Node } from "@xyflow/react";
import {
    type BuildFacetGraphParams,
    type ExplodeFlow,
    type ExplodeNodeData,
    NodeRole,
} from "../../types";
import { columnsForAspect } from "./columnsForAspect";
import {
    COLUMN_GAP,
    GRID_TOP,
    NODE_WIDTH,
    OVERLAY_PAGE_SIZE,
    ROOT_Y,
    ROW_GAP,
} from "./graphLayout";
import { graphNode } from "./graphNode";

export const buildFieldGrid = ({
    fieldOptions,
    rootLabel,
    gridPage,
}: {
    fieldOptions: NonNullable<BuildFacetGraphParams["fieldOptions"]>;
    rootLabel: string;
    gridPage: number;
}): ExplodeFlow => {
    const start = gridPage * OVERLAY_PAGE_SIZE;
    const pageOptions = fieldOptions.slice(start, start + OVERLAY_PAGE_SIZE);
    const remaining = fieldOptions.length - (start + pageOptions.length);
    const cellCount = pageOptions.length + (remaining > 0 ? 1 : 0) + (gridPage > 0 ? 1 : 0);

    const columns = columnsForAspect(cellCount || 1);
    const stepX = NODE_WIDTH + COLUMN_GAP;
    const gridWidth = Math.min(cellCount || 1, columns) * stepX - COLUMN_GAP;
    const gridCenterX = (gridWidth - NODE_WIDTH) / 2;

    const fieldNodes = pageOptions.map((option, index) =>
        graphNode(
            `f-${index}`,
            (index % columns) * stepX,
            GRID_TOP + Math.floor(index / columns) * ROW_GAP,
            {
                label: option.label,
                count: option.count,
                role: NodeRole.GROUP,
                categoryField: option.field,
                selection: [],
            },
        ),
    );

    const nextNode: Node<ExplodeNodeData>[] =
        remaining > 0
            ? [
                  graphNode(
                      "grid-next",
                      (pageOptions.length % columns) * stepX,
                      GRID_TOP + Math.floor(pageOptions.length / columns) * ROW_GAP,
                      {
                          label: `…next ${Math.min(OVERLAY_PAGE_SIZE, remaining)} (${remaining.toLocaleString()} left)`,
                          role: NodeRole.GROUP,
                          selection: [],
                          muted: true,
                          gridPage: gridPage + 1,
                      },
                  ),
              ]
            : [];

    const backIndex = pageOptions.length + (remaining > 0 ? 1 : 0);
    const backNode: Node<ExplodeNodeData>[] =
        gridPage > 0
            ? [
                  graphNode(
                      "grid-back",
                      (backIndex % columns) * stepX,
                      GRID_TOP + Math.floor(backIndex / columns) * ROW_GAP,
                      {
                          label: "…back",
                          role: NodeRole.GROUP,
                          selection: [],
                          muted: true,
                          gridPage: gridPage - 1,
                      },
                  ),
              ]
            : [];

    const rootNode = graphNode("root", gridCenterX, ROOT_Y, {
        label: rootLabel,
        role: NodeRole.ROOT,
        selection: [],
    });

    const fieldEdges = pageOptions.map<Edge>((_, index) => ({
        id: `e-root-f${index}`,
        source: "root",
        target: `f-${index}`,
        type: "default",
        style: { stroke: "#c0b6e8", strokeWidth: 1 },
    }));

    return {
        nodes: [rootNode, ...fieldNodes, ...nextNode, ...backNode],
        edges: fieldEdges,
    };
};
